import { differenceInDays, parseISO } from 'date-fns';
import { loanTypeReasonValue } from './models';
export const isCreditCardType = (type) => type === 'CREDIT_CARD';
const createdAtMs = (enquiry) => {
    const time = enquiry.createdAt
        ? parseISO(enquiry.createdAt).getTime()
        : Number.NaN;
    return Number.isNaN(time) ? -Infinity : time;
};
/**
 * Newest first. The endpoint already returns them this way; sorting on
 * `createdAt` keeps the newest row well defined now that expired rows are
 * included too. Rows without a usable timestamp go last, in the given order.
 */
const newestFirst = (enquiries) => [...enquiries].sort((a, b) => {
    const aTime = createdAtMs(a);
    const bTime = createdAtMs(b);
    return aTime === bTime ? 0 : bTime > aTime ? 1 : -1;
});
const loanPurposeOf = (enquiry) => enquiry.metadata?.qs?.loan_purpose;
/**
 * Whether an enquiry was for the kind of product the user has just asked for.
 * A generic loan matches any enquiry that was not for a credit card — tested
 * explicitly rather than through its purpose code being zero, and therefore
 * falsy.
 */
const matchesRequestedType = (enquiry, type) => {
    const purpose = loanPurposeOf(enquiry);
    if (isCreditCardType(type)) {
        return purpose === loanTypeReasonValue.CREDIT_CARD;
    }
    if (!type || type === 'LOAN') {
        return purpose !== loanTypeReasonValue.CREDIT_CARD;
    }
    return purpose === loanTypeReasonValue[type];
};
const toAmount = (value) => {
    if (value == null)
        return undefined;
    const amount = Math.floor(Number(value));
    return amount > 0 ? String(amount) : undefined;
};
const toTerm = (value) => {
    if (value == null)
        return undefined;
    const term = Number(value);
    return Number.isFinite(term) && term > 0 ? term : undefined;
};
/**
 * What the loan amount screen opens with. Amount and term come from the
 * enquiry that was for the same thing the user has just asked for, else the
 * newest one. The reason comes from the entry point when it names one — a user
 * who tapped "consolidate debt" has stated their purpose in that tap, and
 * prefilling last time's would fight the screen's own behaviour. Otherwise it
 * comes only from a matching enquiry, so a generic loan never inherits a
 * credit card's purpose.
 */
export const monevoPrefillFromEnquiries = (enquiries, type) => {
    const rows = newestFirst(enquiries);
    const match = rows.find((enquiry) => matchesRequestedType(enquiry, type));
    const source = match ?? rows.at(0);
    const previousPurpose = match ? loanPurposeOf(match) : undefined;
    return {
        loanAmount: toAmount(source?.metadata?.qs?.loan_amount),
        loanTerm: toTerm(source?.metadata?.qs?.loan_term),
        loanPurpose: type && type !== 'LOAN' ? loanTypeReasonValue[type] : previousPurpose,
    };
};
/** Whole days since the enquiry we are reusing, for the submission event. */
export const monevoPreviousQuoteAgeDays = (enquiries) => {
    const createdAt = newestFirst(enquiries).at(0)?.createdAt;
    if (!createdAt)
        return undefined;
    const parsed = parseISO(createdAt);
    return Number.isNaN(parsed.getTime())
        ? undefined
        : differenceInDays(new Date(), parsed);
};
/**
 * Where a loan or credit-card tap goes. Data in, data out: the caller owns the
 * navigation, the feature flag and the country check.
 *
 * Rule one is purpose-matched and rule two is purpose-blind, which is what
 * produces the cross-product behaviour — a live loan quote plus a credit-card
 * tap falls past the modal into reuse, and the reverse holds too.
 */
export const resolveMonevoEntry = (enquiries, type, { skipQuotesModal } = {}) => {
    const rows = newestFirst(enquiries);
    if (!skipQuotesModal) {
        /**
         * Expired rows now arrive carrying a present but dead url, so the active
         * flag has to be honoured before any url is read.
         */
        const live = rows.find((enquiry) => enquiry.isActive &&
            enquiry.metadata?.url &&
            matchesRequestedType(enquiry, type));
        const url = live?.metadata?.url;
        if (url)
            return { kind: 'quotesModal', url };
    }
    /**
     * Reuse eligibility is a per-user answer the backend computes against what
     * it has saved right now, so it is only meaningful on the newest row.
     */
    if (rows.at(0)?.canReuseDetails) {
        return {
            kind: 'reuse',
            prefill: monevoPrefillFromEnquiries(enquiries, type),
        };
    }
    return { kind: 'funnel' };
};
